import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon, 
  CheckCircleIcon, 
  ArrowLeftIcon,
  ExclamationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface DealDocument {
  name: string;
  description: string;
  icon: React.ReactNode;
}

const DealDocuments: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ceremonyUrl, setCeremonyUrl] = useState<string | null>(null);
  const [envelopeId, setEnvelopeId] = useState<string | null>(null);
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const [signatureCompleted, setSignatureCompleted] = useState(false);

  const documents: DealDocument[] = [
    {
      name: 'Master Facility Agreement',
      description: 'The primary agreement outlining all terms and conditions of your equity facility',
      icon: <DocumentTextIcon className="h-6 w-6" />
    },
    {
      name: 'Registration Rights Agreement',
      description: 'Agreement detailing your obligations regarding SEC registration of shares',
      icon: <DocumentTextIcon className="h-6 w-6" />
    },
    {
      name: 'Secretary Certificate',
      description: 'Corporate authorization certificate confirming board approval',
      icon: <DocumentTextIcon className="h-6 w-6" />
    }
  ];

  useEffect(() => {
    // Get facility ID from user data
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setFacilityId(user.facilityId);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const handlePrepareDocuments = async () => {
    if (!facilityId) {
      setError('No facility ID found');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/signature/deal-documents/${facilityId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Check if manual signing is required
        if (response.data.data.status === 'manual_signing_required') {
          setError(null);
          // Show manual signing message
          alert(response.data.data.message || 'Please download and sign the documents manually.');
          
          // Optionally redirect to download page
          if (response.data.data.documentPath) {
            window.location.href = `/api/signature/download-docx/${facilityId}`;
          }
        } else if (response.data.data.ceremonyUrl) {
          setCeremonyUrl(response.data.data.ceremonyUrl);
          setEnvelopeId(response.data.data.envelopeId);
        } else {
          setError('No signing URL received');
        }
      } else {
        setError('Failed to prepare documents');
      }
    } catch (error: any) {
      console.error('Error preparing documents:', error);
      setError(error.response?.data?.message || 'Failed to prepare documents for signature');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignatureComplete = () => {
    setSignatureCompleted(true);
    // Give user a moment to see success message before redirecting
    setTimeout(() => {
      navigate('/dashboard');
    }, 2000);
  };

  // Listen for postMessage events from SignatureAPI iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify the message is from SignatureAPI
      if (event.origin !== 'https://app.signatureapi.com') return;

      const { type, data } = event.data;
      
      if (type === 'signature_completed') {
        handleSignatureComplete();
      } else if (type === 'signature_error') {
        setError('An error occurred during signing. Please try again.');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  if (signatureCompleted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center"
        >
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Documents Signed Successfully!
          </h2>
          <p className="text-gray-600 mb-4">
            Your facility agreement has been executed. Redirecting to dashboard...
          </p>
        </motion.div>
      </div>
    );
  }

  if (ceremonyUrl) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">Sign Your Deal Documents</h1>
              <button
                onClick={() => navigate('/pre-closing-checklist')}
                className="text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-green-800">
              <strong>Ready to Sign:</strong> All signature fields have been placed for you. 
              Simply click on each field to sign, initial, and date the document.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <iframe
              src={ceremonyUrl}
              className="w-full"
              style={{ height: 'calc(100vh - 280px)' }}
              frameBorder="0"
              allow="camera; microphone"
              title="SignNow Document Signing"
              onLoad={() => console.log('SignNow iframe loaded successfully')}
            />
          </div>
          <div className="mt-4 text-center text-sm text-gray-600">
            <p>Having trouble? You can also check your email for a signing link.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/pre-closing-checklist')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Review and Sign Deal Documents</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
          >
            <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Documents to be Signed</h2>
          <p className="text-gray-600 mb-6">
            The following documents will be prepared and presented for your electronic signature:
          </p>

          <div className="space-y-4 mb-8">
            {documents.map((doc, index) => (
              <motion.div
                key={doc.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg"
              >
                <div className="p-2 bg-white rounded-lg">
                  {doc.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{doc.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-900 mb-2">Important Information</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• All documents will be merged with your facility details</li>
              <li>• You'll be able to review each document before signing</li>
              <li>• The signing process is secure and legally binding</li>
              <li>• You'll receive copies of all signed documents via email</li>
            </ul>
          </div>

          <div className="flex justify-end gap-4">
            <button
              onClick={() => navigate('/pre-closing-checklist')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back to Checklist
            </button>
            <button
              onClick={handlePrepareDocuments}
              disabled={isLoading}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  Preparing Documents...
                </>
              ) : (
                <>
                  <DocumentTextIcon className="h-5 w-5" />
                  Proceed to Signature
                </>
              )}
            </button>
          </div>
        </motion.div>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            By proceeding, you acknowledge that you have the authority to sign these documents
            on behalf of your company.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DealDocuments; 